/**
 * FillHandle.tsx
 * Small 6×6 square handle positioned at the bottom-right corner of a focused
 * skill matrix cell. Dragging it triggers Excel-style autofill via useDragFill.
 *
 * The cell's TableCell must have `position: relative` to anchor this correctly.
 */

import React from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface FillHandleProps {
  onMouseDown: (e: React.MouseEvent) => void;
}

export const FillHandle: React.FC<FillHandleProps> = ({ onMouseDown }) => {
  const theme = useTheme();

  return (
    <Box
      className="skill-fill-handle"
      onMouseDown={onMouseDown}
      sx={{
        position: 'absolute',
        bottom: -3,
        right: -3,
        width: 6,
        height: 6,
        bgcolor: theme.palette.primary.main,
        cursor: 'crosshair',
        zIndex: 5,
        // Prevent the box from intercepting pointer events on the cell itself
        // when the user is not hovering directly over the handle.
        pointerEvents: 'auto',
        borderRadius: '1px',
        // Hidden by default; revealed when the parent cell is hovered
        // (parent cell's sx adds '&:hover .skill-fill-handle': { opacity: 1 }).
        opacity: 0,
        '&:hover': {
          transform: 'scale(1.4)',
        },
        // Smooth opacity + scale animation.
        transition: 'opacity 0.1s ease, transform 0.1s ease',
      }}
    />
  );
};
